<template>
  <div class="results1-wrap">
    <main class="screen" role="main">

      <span class="badge">Dein Ergebnis</span>

      <div class="gauge-block">
        <div class="gauge" role="img" :aria-label="`Gesamtscore ${overallScore} von ${overallMax}`">
          <svg viewBox="0 0 200 200">
            <circle class="gauge-track" cx="100" cy="100" r="90"/>
            <circle
              class="gauge-fill"
              cx="100"
              cy="100"
              r="90"
              :stroke-dashoffset="gaugeOffset"
            />
          </svg>
          <div class="gauge-text">
            <div class="gauge-score">{{ overallScore }}</div>
            <div class="gauge-max">von {{ overallMax }}</div>
          </div>
        </div>
        <p class="verbal-label">{{ verbalLabel }}</p>
        <button type="button" class="meaning-link" @click="onMeaningClick">
          Was bedeutet das?
        </button>
      </div>

      <h2 class="section-heading">Deine Lebensmittelgruppen</h2>

      <div class="bar-list">
        <div v-for="g in groups" :key="g.id" class="bar-row">
          <span class="bar-emoji">{{ g.emoji }}</span>
          <div class="bar-track">
            <div
              class="bar-fill"
              :class="statusFor(g.score)"
              :style="{ width: g.score + '%' }"
            ></div>
          </div>
          <span class="bar-value">{{ g.score }}</span>
        </div>
      </div>

      <h2 class="section-heading">Im Detail</h2>

      <div class="group-list">
        <details
          v-for="g in groups"
          :key="g.id"
          class="group"
          :open="g.id === 'pflanz-prot'"
        >
          <summary class="group-summary">
            <span class="group-emoji">{{ g.emoji }}</span>
            <span class="group-name">{{ g.name }}</span>
            <span class="group-score" :class="statusFor(g.score)">{{ g.score }}</span>
            <span class="group-chevron">▾</span>
          </summary>
          <div class="group-detail">
            <p class="group-feedback">{{ g.feedback }}</p>
            <div class="suggestion">
              <span class="suggestion-icon">💡</span>
              <span>{{ g.suggestion }}</span>
            </div>
          </div>
        </details>
      </div>

      <div class="footer">
        <button type="button" class="next-button" @click="goNext">Weiter</button>
      </div>

      <p class="placeholder-note">
        Beispieltexte. Die finalen Formulierungen werden vom Ernährungsteam erstellt.
      </p>

    </main>
  </div>
</template>

<script>
import { overallScore, overallMax, verbalLabel, groups, statusFor } from '../_data/sampleResults.js';

const GAUGE_CIRCUMFERENCE = 565;

export default {
  name: 'Results1',
  data() {
    return { overallScore, overallMax, verbalLabel, groups };
  },
  computed: {
    gaugeOffset() {
      return GAUGE_CIRCUMFERENCE * (1 - this.overallScore / this.overallMax);
    },
  },
  methods: {
    statusFor,
    goNext() {
      this.$router.push('/postsurvey/email');
    },
    onMeaningClick() {
      // Placeholder for "Was bedeutet das?" — the real explainer modal
      // will be wired up once the Ernährungsteam delivers copy.
    },
  },
};
</script>

<style scoped>
.results1-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --color-good: #639922;
  --color-good-light: #EAF3DE;
  --color-warn: #BA7517;
  --color-warn-light: #FAEEDA;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.results1-wrap *,
.results1-wrap *::before,
.results1-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem 1.25rem;
  min-height: 600px;
}

.badge {
  display: inline-block;
  background: var(--color-accent-light);
  color: var(--color-accent);
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: var(--radius-md);
  margin-bottom: 1.5rem;
  letter-spacing: 0.02em;
}

.gauge-block {
  text-align: center;
  margin-bottom: 1.5rem;
}

.gauge {
  position: relative;
  width: 200px;
  height: 200px;
  margin: 0 auto 1rem;
}

.gauge svg {
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
}

.gauge-track {
  fill: none;
  stroke: var(--color-bg-muted);
  stroke-width: 14;
}

.gauge-fill {
  fill: none;
  stroke: var(--color-accent);
  stroke-width: 14;
  stroke-linecap: round;
  stroke-dasharray: 565;
  transition: stroke-dashoffset 1s ease-out;
}

.gauge-text {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.gauge-score {
  font-size: 44px;
  font-weight: 500;
  line-height: 1;
  color: var(--color-text-primary);
}

.gauge-max {
  font-size: 14px;
  color: var(--color-text-tertiary);
  margin-top: 4px;
}

.verbal-label {
  font-size: 18px;
  font-weight: 500;
  color: var(--color-accent);
  margin: 0 0 8px;
}

.meaning-link {
  font-size: 13px;
  color: var(--color-text-secondary);
  background: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-family: inherit;
}

.section-heading {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin: 1.5rem 0 0.75rem;
}

.bar-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 1.5rem;
}

.bar-row {
  display: grid;
  grid-template-columns: 28px 1fr 36px;
  align-items: center;
  gap: 10px;
}

.bar-emoji {
  font-size: 18px;
  text-align: center;
  line-height: 1;
}

.bar-track {
  height: 10px;
  background: var(--color-bg-muted);
  border-radius: 5px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: var(--color-accent);
  border-radius: 5px;
}

.bar-fill.good { background: var(--color-good); }
.bar-fill.warn { background: var(--color-warn); }

.bar-value {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-align: right;
}

.group-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  overflow: hidden;
}

.group-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  cursor: pointer;
  list-style: none;
}

.group-summary::-webkit-details-marker { display: none; }

.group-emoji {
  font-size: 20px;
  line-height: 1;
}

.group-name {
  flex: 1;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.group-score {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  padding: 2px 8px;
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
}

.group-score.good { background: var(--color-good-light); color: var(--color-good); }
.group-score.warn { background: var(--color-warn-light); color: var(--color-warn); }

.group-chevron {
  color: var(--color-text-tertiary);
  transition: transform 0.2s;
}

details[open] .group-chevron {
  transform: rotate(180deg);
}

.group-detail {
  padding: 12px 14px 14px;
  border-top: 0.5px solid var(--color-border);
}

.group-feedback {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  margin: 0 0 12px;
}

.suggestion {
  background: var(--color-accent-light);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  font-size: 13px;
  color: var(--color-accent);
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.suggestion-icon {
  flex-shrink: 0;
  font-size: 14px;
  margin-top: 1px;
}

.footer {
  margin-top: 1.5rem;
}

.next-button {
  width: 100%;
  padding: 14px;
  background: var(--color-accent);
  color: white;
  border: 0.5px solid var(--color-accent);
  border-radius: var(--radius-md);
  font-size: 15px;
  font-family: inherit;
  cursor: pointer;
}

.next-button:hover { opacity: 0.9; }

.placeholder-note {
  font-size: 11px;
  color: var(--color-text-tertiary);
  text-align: center;
  margin-top: 12px;
  font-style: italic;
}
</style>
